import { useState } from 'react';
import { useTenantCollection } from '../hooks/useTenantData';
import { useClub } from '../contexts/ClubContext';

function Gallery() {
  const { club } = useClub();
  const { data: albums, loading } = useTenantCollection('galleries');
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  // Sort albums by date (newest first)
  const sortedAlbums = [...albums]
    .filter(a => a.isPublic !== false) // Only show public albums
    .sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
      return dateB - dateA;
    });

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-TT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = 'auto';
  };

  const nextPhoto = () => {
    if (selectedAlbum && lightboxIndex < selectedAlbum.photos.length - 1) {
      setLightboxIndex(lightboxIndex + 1);
    }
  };

  const prevPhoto = () => {
    if (lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (lightboxIndex === null) return;
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextPhoto();
    if (e.key === 'ArrowLeft') prevPhoto();
  };

  return (
    <div className="gallery-page" onKeyDown={handleKeyDown} tabIndex="0">
      {/* Hero Section */}
      <section className="gallery-hero">
        <h1>PHOTO GALLERY</h1>
        <p>Memories from {club?.name || 'ClubLink TT'} events, matches, and training sessions</p>
      </section>

      {/* Albums or Album View */}
      <section className="gallery-content">
        {loading ? (
          <div className="gallery-loading">
            <div className="loading-spinner"></div>
            <p>Loading gallery...</p>
          </div>
        ) : selectedAlbum ? (
          // Single Album View
          <div className="album-view">
            <button className="btn-back-gallery" onClick={() => setSelectedAlbum(null)}>
              ← Back to Albums
            </button>
            <div className="album-header">
              <h2>{selectedAlbum.title}</h2>
              <p className="album-date">{formatDate(selectedAlbum.createdAt)}</p>
              {selectedAlbum.description && (
                <p className="album-description">{selectedAlbum.description}</p>
              )}
              <p className="photo-count">{selectedAlbum.photos?.length || 0} photos</p>
            </div>
            
            {selectedAlbum.photos && selectedAlbum.photos.length > 0 ? (
              <div className="photos-grid">
                {selectedAlbum.photos.map((photo, index) => (
                  <div 
                    key={index} 
                    className="photo-item"
                    onClick={() => openLightbox(index)}
                  >
                    <img src={photo.url} alt={photo.caption || `Photo ${index + 1}`} />
                    {photo.caption && <span className="photo-caption">{photo.caption}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-photos">
                <span className="empty-icon">📷</span>
                <p>No photos in this album yet</p>
              </div>
            )}
          </div>
        ) : sortedAlbums.length === 0 ? (
          // No Albums
          <div className="no-albums">
            <span className="empty-icon">📸</span>
            <h3>No Photo Albums Yet</h3>
            <p>Check back soon for photos from our events and matches!</p>
          </div>
        ) : (
          // Albums Grid
          <div className="albums-grid">
            {sortedAlbums.map(album => (
              <div 
                key={album.id} 
                className="album-card"
                onClick={() => setSelectedAlbum(album)}
              >
                <div className="album-cover">
                  {album.coverPhoto || (album.photos && album.photos[0]) ? (
                    <img 
                      src={album.coverPhoto || album.photos[0].url} 
                      alt={album.title} 
                    />
                  ) : (
                    <div className="album-placeholder">
                      <span>📷</span>
                    </div>
                  )}
                  <div className="album-photo-count">
                    {album.photos?.length || 0} photos
                  </div>
                </div>
                <div className="album-info">
                  <h3>{album.title}</h3>
                  <p className="album-date">{formatDate(album.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && selectedAlbum && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>×</button>
          <button 
            className="lightbox-prev" 
            onClick={(e) => { e.stopPropagation(); prevPhoto(); }}
            disabled={lightboxIndex === 0}
          >
            ‹
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img 
              src={selectedAlbum.photos[lightboxIndex].url} 
              alt={selectedAlbum.photos[lightboxIndex].caption || ''} 
            />
            {selectedAlbum.photos[lightboxIndex].caption && (
              <p className="lightbox-caption">{selectedAlbum.photos[lightboxIndex].caption}</p>
            )}
            <p className="lightbox-counter">
              {lightboxIndex + 1} / {selectedAlbum.photos.length}
            </p>
          </div>
          <button 
            className="lightbox-next" 
            onClick={(e) => { e.stopPropagation(); nextPhoto(); }}
            disabled={lightboxIndex === selectedAlbum.photos.length - 1}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}

export default Gallery;
